import React from 'react';
import cn from 'clsx';
import { css } from 'linaria';
import { styled } from 'linaria/react';
import { memo } from 'common/hocs/memo';
import { Typography, TypographyProps } from '../typography';
import { SectionSubTitle, SectionSubTitleProps } from '../section-subtitle';
import {
  Link,
  LinkProps,
  LinkWithGAClickButton,
  LinkWithGAClickButtonProps,
  LinkWithGAPromoClick,
  LinkWithGAPromoClickProps,
} from '../link';

const cardLink = css`
  &:hover {
    box-shadow: var(--box-shadow-m);
  }
`;

export const CardLink = (props: LinkProps) => <Link className={cardLink} {...props} />;

export const CardLinkWithGAClickButton = (props: LinkWithGAClickButtonProps) => (
  <LinkWithGAClickButton className={cardLink} {...props} />
);

export const CardLinkWithGAPromoClick = (props: LinkWithGAPromoClickProps) => (
  <LinkWithGAPromoClick className={cardLink} {...props} />
);

export const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  height: 100%;
  padding: 32px;
  color: var(--text-primary);
  background-color: var(--background-primary);
  border-radius: var(--rounding-l);
  box-shadow: var(--box-shadow-m);

  @media (--only-tablet) {
    padding: 24px;
  }

  @media (--only-mobile) {
    width: 100%;
    min-height: auto;
    padding: 96px 24px 24px;
  }
`;

export const CardGrid = styled.div`
  display: grid;
  justify-content: space-between;
  grid-template-columns: repeat(2, 1fr);

  @media (--only-desktop) {
    grid-template-columns: repeat(2, 2fr);
  }

  @media (--from-tablet) {
    grid-column-gap: 24px;
  }

  @media (--only-mobile) {
    grid-row-gap: 24px;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, 1fr);
  }
`;

export const CardHeader = memo(
  ({ as = 'h3', children, className, variant = 'h3', ...rest }: Omit<SectionSubTitleProps, 'margin'>) => (
    <SectionSubTitle adaptiveMargin className={className} variant={variant} as={as} margin="s" {...rest}>
      {children}
    </SectionSubTitle>
  )
);

export const cardHeaderStyle = css`
  @media (--only-tablet) {
    margin-bottom: 8px;
  }

  @media (--only-mobile) {
    margin-bottom: 12px;
  }
`;

type CardDescriptionProps = Omit<TypographyProps, 'variant' | 'size'>;

export const CardDescription = memo(({ children, className, ...rest }: CardDescriptionProps) => (
  <Typography className={cn(className, cardDescriptionStyle)} variant="m" size="body_long" {...rest}>
    {children}
  </Typography>
));

export const cardDescriptionStyle = css`
  margin-bottom: 24px;

  @media (--only-largeDesktop) {
    white-space: break-spaces;
  }
`;

export const CardLogo = styled.img`
  position: absolute;
  top: 32px;
  right: 32px;
  width: 80px;
  height: 80px;
  background-repeat: no-repeat;
  background-position: 50%;
  background-size: contain;

  @media (--to-desktop) {
    top: auto;
    right: 24px;
    bottom: 24px;
    width: 60px;
    height: 60px;
  }

  @media (--only-mobile) {
    inset: 24px auto auto 24px;
  }
`;

export const CardActions = styled.div`
  width: 100%;
  margin-top: auto;
`;
